import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuthStore, type Me } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';

interface HandoffResponse {
  accessToken: string;
  refreshToken: string;
  user: Me;
  redirectPath: string;
}

// One-time SSO landing for cross-app jumps (Wapplify Office → WapStat).
export function SsoHandoff(): JSX.Element {
  const [error, setError] = useState(false);
  // Guard against React 18 StrictMode double-invoke — the token is one-time,
  // so a second POST would 401 and wrongly show the error state.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setError(true); return; }

    void (async () => {
      try {
        const r = await axios.post<HandoffResponse>('/api/auth/handoff', { token });
        // Store the session exactly as the login flow does.
        useAuthStore.setState({
          accessToken: r.data.accessToken,
          refreshToken: r.data.refreshToken,
          user: r.data.user,
        });
        // Only a single leading slash (same-origin path) — reject protocol-relative
        // (`//host`) and backslash (`/\host`) paths to prevent an open redirect.
        const dest = /^\/(?![/\\]).*$/.test(r.data.redirectPath ?? '')
          ? r.data.redirectPath
          : '/';
        // HARD navigation (not SPA): clean reload so the switched-in session's token,
        // a fresh query cache, and a fresh hydrateMe all start from the new user.
        window.location.assign(dest);
      } catch {
        setError(true);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      {error ? (
        <div
          role="alert"
          className="w-full max-w-sm rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-4 text-center text-sm text-destructive"
        >
          {dv.auth.handoffInvalid}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-muted-foreground" role="status" aria-live="polite">
          <span
            aria-hidden
            className="size-6 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
          <span className="text-sm">{dv.auth.signingIn}</span>
        </div>
      )}
    </div>
  );
}
